import { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Users, UserCheck, Clock, MapPin, AlertTriangle, Coffee, CalendarOff, Download, ChevronLeft, ChevronRight, Loader2, X, Home, CalendarX2, Palmtree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate, formatTime } from '@/lib/dateUtils';
import { toast } from 'sonner';
import { useBranchFilter } from '@/components/BranchFilterContext';

function SummaryCard({ icon: Icon, label, value, color, items, emptyText }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white rounded-xl border border-border/30 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon size={18} />
          </div>
          <div>
            <p className="text-2xl font-extrabold text-[#1a2529]">{value}</p>
            <p className="text-xs text-[#1a2529]/50 font-medium">{label}</p>
          </div>
        </div>
        {items && items.length > 0 && (
          <button onClick={() => setOpen(!open)} className="text-xs text-[#026536] font-semibold hover:underline">
            {open ? 'Hide' : 'View'}
          </button>
        )}
      </div>
      {open && items && (
        <div className="mt-3 border-t pt-3 space-y-1.5 max-h-48 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-xs text-[#1a2529]/40">{emptyText || 'None'}</p>
          ) : items.map((item, i) => (
            <div key={item.id || `${item.name}-${i}`} className="flex items-center justify-between text-xs">
              <span className="font-medium text-[#1a2529]">{item.name}</span>
              <span className="text-[#1a2529]/50">{item.phone || item.detail || ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDailySummary() {
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calOpen, setCalOpen] = useState(false);
  const { branch } = useBranchFilter();

  const dateStr = date.toISOString().split('T')[0];

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getDailySummary(dateStr, branch);
      setData(res.data);
    } catch {
      toast.error('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  }, [dateStr, branch]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const shiftDate = (days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setDate(d);
  };

  const downloadSummary = () => {
    if (!data) return;
    const lines = [];
    lines.push(`UPASTITH Daily Summary Report - ${formatDate(data.date)}`);
    lines.push('');
    lines.push(`Total Employees,${data.total_employees}`);
    lines.push(`Checked In,${data.checked_in}`);
    lines.push(`On Leave,${data.on_leave}`);
    lines.push(`Absent,${data.absent}`);
    lines.push(`Late Arrivals,${data.late_count}`);
    lines.push(`Early Departures,${data.early_departure_count}`);
    lines.push(`Outside Zone,${data.outside_zone_count}`);
    lines.push(`Not Checked Out,${data.not_checked_out}`);
    lines.push(`Overtime,${data.overtime_count}`);
    lines.push('');
    lines.push(`Pending Leave Requests,${data.pending_requests?.leaves || 0}`);
    lines.push(`Pending Sanction Requests,${data.pending_requests?.sanctions || 0}`);
    lines.push(`Pending Work Permits,${data.pending_requests?.permits || 0}`);

    if (data.late_list?.length) {
      lines.push('');
      lines.push('Late Arrivals');
      lines.push('Name,Phone,Time');
      data.late_list.forEach(e => lines.push(`${e.name},${e.phone},${e.time}`));
    }
    if (data.absent_list?.length) {
      lines.push('');
      lines.push('Absent Employees');
      lines.push('Name,Phone');
      data.absent_list.forEach(e => lines.push(`${e.name},${e.phone}`));
    }
    if (data.on_leave_list?.length) {
      lines.push('');
      lines.push('On Leave');
      lines.push('Name,Phone,Type');
      data.on_leave_list.forEach(e => lines.push(`${e.name},${e.phone},${e.type}`));
    }
    if (data.not_checked_out_list?.length) {
      lines.push('');
      lines.push('Not Checked Out');
      lines.push('Name,Phone,Check-in Time');
      data.not_checked_out_list.forEach(e => lines.push(`${e.name},${e.phone},${e.checkin_time}`));
    }
    if (data.attendance_records?.length) {
      lines.push('');
      lines.push('All Attendance Records');
      lines.push('Name,Phone,Check-in,Check-out,Late,Zone');
      data.attendance_records.forEach(r =>
        lines.push(`${r.user_name || ''},${r.user_phone || ''},${r.timestamp || ''},${r.checkout_timestamp || ''},${r.is_late ? 'Yes' : 'No'},${r.zone_name || ''}`)
      );
    }

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily_summary_${data.date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Summary downloaded');
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 data-testid="daily-summary-title" className="text-2xl font-extrabold text-[#1a2529]">Daily Summary</h1>
            <p className="text-sm text-[#1a2529]/50">Overview for {formatDate(dateStr)}</p>
          </div>
          <Button data-testid="download-summary-btn" onClick={downloadSummary} disabled={!data} size="sm" className="bg-[#026536] hover:bg-[#026536]/90 gap-1.5 font-semibold">
            <Download size={14} /> Download CSV
          </Button>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center gap-2 bg-white rounded-xl p-3 border border-border/30 shadow-sm">
          <button data-testid="prev-date" onClick={() => shiftDate(-1)} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5] text-[#1a2529]/60">
            <ChevronLeft size={18} />
          </button>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <button data-testid="date-picker" className="flex-1 text-center font-semibold text-[#1a2529] text-sm hover:bg-[#f4f4f5] rounded-lg py-1.5 transition-colors">
                {formatDate(dateStr)}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setCalOpen(false); } }} />
            </PopoverContent>
          </Popover>
          <button data-testid="next-date" onClick={() => shiftDate(1)} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5] text-[#1a2529]/60">
            <ChevronRight size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#026536]" size={28} /></div>
        ) : !data ? (
          <div className="bg-white rounded-xl p-8 text-center"><p className="text-[#1a2529]/50">No data available</p></div>
        ) : (
          <>
            {/* Summary Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <SummaryCard icon={Users} label="Total Employees" value={data.total_employees} color="bg-[#026536]/10 text-[#026536]" />
              <SummaryCard icon={UserCheck} label="Checked In" value={data.checked_in} color="bg-emerald-50 text-emerald-600"
                items={data.attendance_records?.map(r => ({ name: r.user_name, detail: r.timestamp?.split('T')[1]?.slice(0, 5) || '' }))} />
              <SummaryCard icon={CalendarOff} label="On Leave" value={data.on_leave} color="bg-blue-50 text-blue-600"
                items={data.on_leave_list?.map(e => ({ name: e.name, detail: e.type }))} />
              <SummaryCard icon={AlertTriangle} label="Absent" value={data.absent} color="bg-red-50 text-red-500"
                items={data.absent_list?.map(e => ({ name: e.name, phone: e.phone }))} />
              {(data.weekly_off > 0) && (
                <SummaryCard icon={CalendarX2} label="Weekly Off" value={data.weekly_off} color="bg-slate-50 text-slate-500" />
              )}
              {(data.holiday_off > 0) && (
                <SummaryCard icon={Palmtree} label="Holiday" value={data.holiday_off} color="bg-amber-50 text-amber-600" />
              )}
              <SummaryCard icon={Clock} label="Late Arrivals" value={data.late_count} color="bg-[#dea439]/10 text-[#dea439]"
                items={data.late_list?.map(e => ({ name: e.name, phone: e.phone }))} />
              <SummaryCard icon={Coffee} label="Early Departures" value={data.early_departure_count} color="bg-orange-50 text-orange-500"
                items={data.early_list?.map(e => ({ name: e.name, phone: e.phone }))} />
              <SummaryCard icon={MapPin} label="Outside Zone" value={data.outside_zone_count} color="bg-purple-50 text-purple-600"
                items={data.outside_zone_list?.map(e => ({ name: e.name, detail: e.zone }))} />
              <SummaryCard icon={Home} label="Work from Home" value={data.wfh_count || 0} color="bg-violet-50 text-violet-600"
                items={data.wfh_list?.map(e => ({ name: e.name, phone: e.phone }))} />
              <SummaryCard icon={Clock} label="Not Checked Out" value={data.not_checked_out} color="bg-slate-100 text-slate-600"
                items={data.not_checked_out_list?.map(e => ({ name: e.name, phone: e.phone }))} />
            </div>

            {/* Pending Requests */}
            {(data.pending_requests?.leaves > 0 || data.pending_requests?.sanctions > 0 || data.pending_requests?.permits > 0) && (
              <div data-testid="pending-requests-section" className="bg-white rounded-xl p-4 border border-border/30 shadow-sm">
                <h3 className="font-bold text-[#1a2529] mb-3 text-sm">Pending Requests</h3>
                <div className="flex flex-wrap gap-3">
                  {data.pending_requests.leaves > 0 && (
                    <Badge className="bg-blue-50 text-blue-600 border-0 text-xs font-semibold px-3 py-1.5">
                      {data.pending_requests.leaves} Leave{data.pending_requests.leaves > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {data.pending_requests.sanctions > 0 && (
                    <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-xs font-semibold px-3 py-1.5">
                      {data.pending_requests.sanctions} Sanction{data.pending_requests.sanctions > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {data.pending_requests.permits > 0 && (
                    <Badge className="bg-purple-50 text-purple-600 border-0 text-xs font-semibold px-3 py-1.5">
                      {data.pending_requests.permits} Permit{data.pending_requests.permits > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Attendance Table */}
            {data.attendance_records?.length > 0 && (
              <div className="bg-white rounded-xl border border-border/30 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-border/20">
                  <h3 className="font-bold text-[#1a2529] text-sm">Attendance Records ({data.attendance_records.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#f4f4f5] text-[#1a2529]/60">
                        <th className="text-left px-4 py-2.5 font-semibold">Employee</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Check-in</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Check-out</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Zone</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/10">
                      {data.attendance_records.map((r, i) => (
                        <AttendanceRow key={r.id || i} record={r} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function AttendanceRow({ record: r }) {
  const [previewImg, setPreviewImg] = useState(null);
  const [loadingSelfie, setLoadingSelfie] = useState(false);

  const loadFullSelfie = async (field) => {
    if (!r.id) return;
    setLoadingSelfie(true);
    try {
      const res = await adminAPI.getAttendanceSelfie(r.id, field);
      if (res.data?.data) {
        setPreviewImg(res.data.data);
      } else {
        toast.error('Selfie not available');
      }
    } catch {
      toast.error('Failed to load selfie');
    } finally {
      setLoadingSelfie(false);
    }
  };

  return (
    <tr className="hover:bg-[#f4f4f5]/50">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          {r.selfie_thumb ? (
            <button onClick={() => loadFullSelfie('selfie')} className="shrink-0" data-testid={`selfie-thumb-${r.user_phone}`}>
              <img src={r.selfie_thumb} alt="" className="h-9 w-9 rounded-full object-cover border-2 border-[#026536]/20 hover:border-[#026536] transition-colors cursor-pointer" />
            </button>
          ) : (
            <div className="h-9 w-9 rounded-full bg-[#f4f4f5] flex items-center justify-center text-[10px] font-bold text-[#1a2529]/30 shrink-0">
              {r.user_name?.[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-medium text-[#1a2529]">{r.user_name}</p>
            <p className="text-[#1a2529]/40">{r.user_phone}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div>
          <p className="text-[#1a2529]/70 font-medium">{r.timestamp ? formatTime(r.timestamp) : '-'}</p>
          {r.has_selfie && (
            <button onClick={() => loadFullSelfie('selfie')} className="text-[10px] text-[#026536] font-semibold hover:underline mt-0.5">
              View photo
            </button>
          )}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div>
          <p className="text-[#1a2529]/70 font-medium">{r.checkout_timestamp && r.checkout_timestamp !== 'None' ? formatTime(r.checkout_timestamp) : '-'}</p>
          {r.has_checkout_selfie && (
            <button onClick={() => loadFullSelfie('checkout_selfie')} className="text-[10px] text-[#026536] font-semibold hover:underline mt-0.5">
              View photo
            </button>
          )}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap gap-1">
          {r.is_late && <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[10px]">Late</Badge>}
          {r.is_early_departure && <Badge className="bg-orange-50 text-orange-500 border-0 text-[10px]">Early</Badge>}
          {r.is_outside_zone && <Badge className="bg-red-50 text-red-500 border-0 text-[10px]">Outside</Badge>}
          {r.is_wfh && <Badge className="bg-violet-50 text-violet-600 border-0 text-[10px]">WFH</Badge>}
          {!r.is_late && !r.is_early_departure && !r.is_outside_zone && (
            <Badge className="bg-[#026536]/10 text-[#026536] border-0 text-[10px]">OK</Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-2.5 text-[#1a2529]/50">{r.zone_name || '-'}</td>
      {(previewImg || loadingSelfie) && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { if (!loadingSelfie) setPreviewImg(null); }}>
          <div className="relative max-w-md w-full animate-slide-up" onClick={e => e.stopPropagation()}>
            {loadingSelfie ? (
              <div className="bg-white rounded-2xl p-12 flex items-center justify-center">
                <Loader2 className="animate-spin text-[#026536]" size={28} />
              </div>
            ) : (
              <>
                <button data-testid="close-selfie-preview" onClick={() => setPreviewImg(null)}
                  className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-white shadow-lg flex items-center justify-center z-10">
                  <X size={16} className="text-[#1a2529]" />
                </button>
                <img src={previewImg} alt="Selfie" className="w-full rounded-2xl shadow-2xl" />
                <p className="text-center text-white/70 text-xs mt-3 font-medium">{r.user_name} &middot; {formatDate(r.date)}</p>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </tr>
  );
}